/**
 * Deduction puzzle — clue by clue, cross out who it cannot be.
 *
 * The schema enforces solvability, so a puzzle with no answer or two answers
 * cannot reach the child: every clue must remove at least one candidate, every
 * candidate must declare every attribute a clue asks about, and applying all
 * the clues must leave exactly the declared solution.
 */

import { z } from 'zod'
import { AttributesSchema, EmojiSchema, IdSchema, LabelSchema, SentenceSchema, isUnique, specFor } from '../common'
import { applyClues, clueEliminationCount } from '../deduction'

export const DeductionCandidateSchema = z.object({
  id: IdSchema,
  label: LabelSchema,
  emoji: EmojiSchema.optional(),
  /** Every attribute any clue can ask about, e.g. { color: "red", home: "forest" }. */
  attributes: AttributesSchema,
})

export const DeductionClueSchema = z.object({
  id: IdSchema,
  /** Read aloud, one sentence: "It does not live in the forest." */
  text: SentenceSchema,
  attribute: LabelSchema,
  value: LabelSchema,
  /** True when the clue rules candidates OUT. Negation is the hard half. */
  negate: z.boolean(),
})

export const DeductionPuzzleConfigSchema = z
  .object({
    /** The question on screen: "Who took the last cookie?" */
    question: SentenceSchema,
    candidates: z.array(DeductionCandidateSchema).min(2).max(8),
    clues: z.array(DeductionClueSchema).min(1).max(6),
    solutionId: IdSchema,
    revealMode: z.enum(['one_at_a_time', 'all_at_once']).default('one_at_a_time'),
    /** Tap a candidate to cross it out — the elimination made visible. */
    allowElimination: z.boolean().default(true),
    /** Deduction is emerging: a wrong guess gets a rethink, never a red cross. */
    celebrateAttempts: z.boolean().default(true),
    /** Offer a "read it again" nudge after a wrong guess. */
    rereadHint: z.boolean().default(true),
  })
  .superRefine((config, ctx) => {
    if (!isUnique(config.candidates.map((candidate) => candidate.id))) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['candidates'], message: 'candidate ids must be unique' })
      return
    }
    if (!isUnique(config.clues.map((clue) => clue.id))) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['clues'], message: 'clue ids must be unique' })
    }
    if (!config.candidates.some((candidate) => candidate.id === config.solutionId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['solutionId'],
        message: `"${config.solutionId}" is not one of the candidates`,
      })
      return
    }

    for (const [index, clue] of config.clues.entries()) {
      const missing = config.candidates.filter(
        (candidate) => candidate.attributes[clue.attribute] === undefined,
      )
      if (missing.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['clues', index, 'attribute'],
          message: `${missing.map((candidate) => `"${candidate.label}"`).join(', ')} do not say what their ${clue.attribute} is, so this clue cannot be applied`,
        })
      }
      if (clueEliminationCount(clue, config.candidates) === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['clues', index],
          message: 'this clue rules nobody out, so it teaches nothing',
        })
      }
    }

    const survivors = applyClues(config.candidates, config.clues)
    if (survivors.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['clues'],
        message: 'the clues contradict each other — nobody is left',
      })
      return
    }
    if (survivors.length > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['clues'],
        message: `the clues leave ${survivors.length} possible answers (${survivors.map((candidate) => candidate.label).join(', ')}) — add a clue`,
      })
      return
    }
    if (survivors[0].id !== config.solutionId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['solutionId'],
        message: `the clues point at "${survivors[0].label}", not at the declared solution`,
      })
    }
  })

export type DeductionPuzzleConfig = z.infer<typeof DeductionPuzzleConfigSchema>

export const DeductionPuzzleSpecSchema = specFor('deduction_puzzle', DeductionPuzzleConfigSchema)
